import React, { useState, useEffect, useRef } from "react";
// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
import Globales from "utils/Globales";
import productStyle from "assets/jss/material-kit-pro-react/views/productStyle.jsx";
import convert from "color-convert";
import { useAuth0 } from "@auth0/auth0-react";
import { useDesingStore } from "stores/desingStore";
import { useUserStore } from "stores/userStore";

function ProductPage(props) {
	const { userState, setUserState } = useUserStore();
	const { desingState, setDesingState } = useDesingStore();

	const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
	const iframeRef = useRef(null);
	const popupLoginRef = useRef(null); // Referencia al popup de login
	const [authToken, setAuthToken] = useState(null);
	const [idModelo, setIdModelo] = useState("");
	const [idTalla, setIdTalla] = useState("");
	const [idUser, setIdUser] = useState("");
	const [nombre, setNombre] = useState("");
	const [descripcion, setDescripcion] = useState("");
	const [idCategoria, setIdCategoria] = useState("");
	const [sessionId, setSessionId] = useState("");
	const [tipo, setTipo] = useState("");
	const [desdePerfil, setDesdePerfil] = useState(false);

	// Obtener token de Auth0
	useEffect(() => {
		const obtenerToken = async () => {
			if (isAuthenticated) {
				try {
					const token = await getAccessTokenSilently({
						authorizationParams: {
							audience: import.meta.env.VITE_AUTH0_AUDIENCE,
							scope: "openid profile email offline_access",
						},
					});
					setAuthToken(token);

					setUserState({ Token: token });
				} catch (error) {
					console.error("Error al obtener token de Auth0:", error);
				}
			}
		};

		obtenerToken();
	}, [isAuthenticated, getAccessTokenSilently, user]);

	// Generar o recuperar sessionId para usuarios anónimos
	useEffect(() => {
		const generarSessionId = async () => {
			// Solo generar sessionId si el usuario NO está autenticado
			if (!isAuthenticated && !idUser) {
				// Verificar si ya existe un sessionId guardado
				let existingSessionId = localStorage.getItem("sessionId");

				if (!existingSessionId) {
					// Generar nuevo sessionId desde el backend
					try {
						const response = await fetch(
							`${Globales.Url}DisenoProductos/DisenoProductos/GenerarSessionId`,
							{
								method: "POST",
								headers: {
									"Content-Type": "application/json",
								},
							}
						);

						const data = await response.json();
						if (!data.error && data.data?.sessionId) {
							existingSessionId = data.data.sessionId;
							localStorage.setItem("sessionId", existingSessionId);
							setSessionId(existingSessionId);
						} else {
							console.error("❌ Error en respuesta de GenerarSessionId:", data);
						}
					} catch (error) {
						console.error("❌ Error al generar SessionId:", error);
					}
				} else {
					setSessionId(existingSessionId);
				}
			}
		};

		generarSessionId();
	}, [isAuthenticated, idUser]);

	// Establecer bandera en estado global cuando se accede a esta ruta
	useEffect(() => {
		// Establecer la bandera en el estado global para indicar que el usuario está en el diseñador
		setDesingState({ returnToDiseñador: true });
		console.log("✅ Bandera returnToDiseñador establecida en estado global");
		
		// Limpiar la bandera cuando el componente se desmonte (si el usuario navega a otra ruta)
		return () => {
			// No limpiar aquí, se limpiará después de la redirección exitosa en LoginAuth0
		};
	}, []);

	// Cargar datos iniciales
	useEffect(() => {
		window.scrollTo(0, 0);
		document.body.scrollTop = 0;
		setTipo(localStorage.getItem("tipo") || "");
		
		// Verificar si hay parámetros guardados del diseñador (después de login)
		// Primero verificar localStorage, luego el estado global
		let paramsPendientes = localStorage.getItem("diseñadorParamsPendiente");
		if (!paramsPendientes && desingState?.diseñadorParamsPendiente) {
			// Si no está en localStorage pero está en el estado global, usarlo
			paramsPendientes = typeof desingState.diseñadorParamsPendiente === 'string' 
				? desingState.diseñadorParamsPendiente 
				: JSON.stringify(desingState.diseñadorParamsPendiente);
		}
		
		if (paramsPendientes) {
			try {
				const params = typeof paramsPendientes === 'string' 
					? JSON.parse(paramsPendientes) 
					: paramsPendientes;
				
				// Los parámetros ya vienen decodificados desde apihelper.js
				// Restaurar parámetros guardados
				setIdModelo(params.id || "");
				setIdTalla(params.idTalla || "");
				setIdUser(userState.id || "");
				setNombre(params.nombre || "");
				setDescripcion(params.descripcion || "");
				setIdCategoria(params.categoria || "");

				// Guardar también en localStorage para compatibilidad
				localStorage.setItem("idModelo", params.id || "");
				localStorage.setItem("idTalla", params.idTalla || "");
				localStorage.setItem("idUser", userState.id || "");
				localStorage.setItem("tipo", params.tipo || "1");

				// Guardar en formato disenioSeleccionado
				const disenioSeleccionado = {
					nombre: params.nombre || "",
					descripcion: params.descripcion || "",
					idCategoriaDiseno: params.categoria || "",
				};
				localStorage.setItem(
					"disenioSeleccionado",
					JSON.stringify(disenioSeleccionado)
				);

				// Guardar también en el estado global para persistencia
				setDesingState({ 
					diseñadorParamsPendiente: params,
					id: params.id || "",
					idSize: params.idTalla || "",
					idModel: params.id || "",
					idCategory: params.categoria || "",
					Name: params.nombre || "",
					Description: params.descripcion || "",
				});

				// Limpiar parámetros pendientes de localStorage (ya están en estado global)
				localStorage.removeItem("diseñadorParamsPendiente");
				console.log("✅ Parámetros del diseñador restaurados y guardados en estado global");
			} catch (e) {
				console.error("Error al restaurar parámetros del diseñador:", e);
				// Si hay error, usar el flujo normal
				cargarDatosNormales();
			}
		} else {
			// Flujo normal sin parámetros guardados
			cargarDatosNormales();
		}

		function cargarDatosNormales() {
			const idTalla = desingState.idSize;
			const idModelo = desingState.id || desingState.idModel;
			const idUser = userState.id;
			setTipo(desingState.type || "1");
			setIdTalla(idTalla);
			setIdModelo(idModelo);
			setIdUser(idUser);
			setNombre(desingState.Name || "");
			setDescripcion(desingState.Description || "");
			setIdCategoria(desingState.idCategory || "");
			// Detectar si viene desde el perfil (type 2 indica edición de diseño existente)
			if (desingState.type == 2 && desingState.id) {
				setDesdePerfil(true);
			}
		}
	}, []);

	// Actualizar idUser cuando el usuario inicie sesión (se actualiza en HeaderLinks)
	// Y cargar el último diseño si viene de un login
	useEffect(() => {
		if (isAuthenticated) {
			// Verificar si debemos cargar un diseño después del login
			const cargarDisenoDespuesLogin = localStorage.getItem("cargarDisenoDespuesLogin");
			
			if (cargarDisenoDespuesLogin === "true") {
				console.log("🔍 Detectado cargarDisenoDespuesLogin, verificando idCliente...");
				
				// Función para cargar el diseño
				const cargarUltimoDiseno = async (idCliente) => {
					if (!idCliente) {
						console.warn("⚠️ No hay idCliente disponible todavía, esperando...");
						return false;
					}
					
					try {
						// Primero intentar usar el ID específico del diseño guardado
						const idDisenoListoFabricar = localStorage.getItem("idDisenoListoFabricar");
						const ultimoDisenoAsociado = localStorage.getItem("ultimoDisenoAsociado");
						
						// Prioridad: idDisenoListoFabricar > ultimoDisenoAsociado > ObtenerUltimoDiseno con filtro
						const idDisenoEspecifico = idDisenoListoFabricar || ultimoDisenoAsociado;
						
						console.log("📥 Obteniendo diseño para idCliente:", idCliente);
						if (idDisenoEspecifico) {
							console.log("📋 Usando ID específico del diseño:", idDisenoEspecifico);
						} else {
							console.log("📋 No hay ID específico, obteniendo último diseño listo para fabricar");
						}
						
						// Obtener el diseño específico o el último diseño listo para fabricar
						const response = await fetch(
							`${Globales.Url}DisenoProductos/DisenoProductos/ObtenerUltimoDiseno`,
							{
								method: "POST",
								headers: {
									"Content-Type": "application/json",
								},
								body: JSON.stringify({
									idCliente: idCliente,
									idDiseno: idDisenoEspecifico || undefined,
									soloListosFabricar: !idDisenoEspecifico, // Solo filtrar si no hay ID específico
								}),
							}
						);

						const data = await response.json();

						if (!data.error && data.data) {
							const ultimoDiseno = data.data;
							console.log("✅ Último diseño obtenido:", ultimoDiseno._id);
							console.log("🔄 Configurando diseño para cargar como rediseño (tipo 2)...");

							// Configurar el diseño como si viniera desde el perfil (rediseñar)
							// Esto usa el mismo flujo que cuando se rediseña desde el perfil
							setTipo("2"); // tipo 2 = diseño existente para edición
							setIdModelo(ultimoDiseno._id); // ID del diseño (no del modelo)
							setIdTalla(ultimoDiseno.idTalla || "");
							setIdUser(idCliente);
							setNombre(ultimoDiseno.nombre || "");
							setDescripcion(ultimoDiseno.descripcion || "");
							setIdCategoria(ultimoDiseno.idCategoriaDiseno || "");
							setDesdePerfil(true); // Marcar como desde perfil para usar el flujo de rediseño

							// Guardar también en localStorage para compatibilidad
							localStorage.setItem("idModelo", ultimoDiseno._id);
							localStorage.setItem("idTalla", ultimoDiseno.idTalla || "");
							localStorage.setItem("idUser", idCliente);
							localStorage.setItem("tipo", "2");

							// Guardar en formato disenioSeleccionado
							const disenioSeleccionado = {
								nombre: ultimoDiseno.nombre || "",
								descripcion: ultimoDiseno.descripcion || "",
								idCategoriaDiseno: ultimoDiseno.idCategoriaDiseno || "",
							};
							localStorage.setItem(
								"disenioSeleccionado",
								JSON.stringify(disenioSeleccionado)
							);

							// Guardar en el estado global para que el iframe lo use
							setDesingState({
								id: ultimoDiseno._id,
								idModel: ultimoDiseno._id,
								type: 2,
								idSize: ultimoDiseno.idTalla || "",
								idCategory: ultimoDiseno.idCategoriaDiseno || "",
								Name: ultimoDiseno.nombre || "",
								Description: ultimoDiseno.descripcion || "",
							});

							// Limpiar las banderas
							localStorage.removeItem("cargarDisenoDespuesLogin");
							localStorage.removeItem("ultimoDisenoAsociado");
							localStorage.removeItem("idDisenoListoFabricar");

							// Recargar el iframe para que use los nuevos parámetros
							console.log("🔄 Recargando iframe con nuevo diseño...");
							if (iframeRef.current) {
								// El iframe se recargará automáticamente cuando cambien las props (urlDiseñador)
								// No necesitamos hacer nada más aquí
							}

							return true;
						} else {
							console.warn("⚠️ No se encontró último diseño o hubo error:", data);
							localStorage.removeItem("cargarDisenoDespuesLogin");
							return false;
						}
					} catch (error) {
						console.error("❌ Error al cargar último diseño:", error);
						localStorage.removeItem("cargarDisenoDespuesLogin");
						return false;
					}
				};
				
				// Verificar periódicamente si el idUser se actualizó
				// (se establece después de que HeaderLinks valida al usuario)
				const checkIdUser = async () => {
					// Obtener idCliente de múltiples fuentes
					let idCliente = userState.id;
					if (!idCliente) {
						const currentIdUser = localStorage.getItem("v");
						if (currentIdUser) {
							idCliente = currentIdUser;
							setIdUser(currentIdUser);
						}
					}
					
					// También verificar idUser del estado local
					if (!idCliente && idUser) {
						idCliente = idUser;
					}
					
					if (idCliente) {
						console.log("✅ idCliente disponible:", idCliente);
						const cargado = await cargarUltimoDiseno(idCliente);
						if (cargado) {
							// Si se cargó exitosamente, limpiar el intervalo
							clearInterval(interval);
							clearTimeout(timeout);
						}
					} else {
						console.log("⏳ Esperando idCliente...");
					}
				};

				// Verificar inmediatamente
				checkIdUser();

				// Verificar cada segundo durante los primeros 15 segundos después del login
				const interval = setInterval(checkIdUser, 1000);
				const timeout = setTimeout(() => {
					clearInterval(interval);
					console.warn("⏰ Timeout esperando idCliente, limpiando cargarDisenoDespuesLogin");
					localStorage.removeItem("cargarDisenoDespuesLogin");
				}, 15000);

				return () => {
					clearInterval(interval);
					clearTimeout(timeout);
				};
			}
		}
	}, [isAuthenticated, idUser, userState.id]);

	// Enviar token al iframe cuando esté disponible o cambie
	useEffect(() => {
		if (authToken && iframeRef.current) {
			// Solo enviar userId si es un ObjectId válido (no enviar user?.sub que es el socialToken)
			const userIdToSend =
				idUser && /^[0-9a-fA-F]{24}$/.test(idUser) ? idUser : null;

			if (userIdToSend) {
				// Enviar token por postMessage al iframe
				const iframe = iframeRef.current;
				if (iframe.contentWindow) {
					iframe.contentWindow.postMessage(
						{
							type: "AUTH0_TOKEN",
							token: authToken,
							userId: userIdToSend,
						},
						"*"
					);
				}
			}
		}
	}, [authToken, idUser, user]);

	// Enviar sessionId al iframe cuando esté disponible
	useEffect(() => {
		if (sessionId && !idUser && iframeRef.current?.contentWindow) {
			iframeRef.current.contentWindow.postMessage(
				{
					type: "SESSION_ID",
					sessionId: sessionId,
				},
				"*"
			);
		}
	}, [sessionId, idUser]);

	// Manejar mensajes del iframe y del popup de login
	useEffect(() => {
		const handleMessage = (event) => {
			// Mensaje del iframe para conversión de color
			if (event.data.type === "funcionHexToCMYK") {
				const valor = convertirHExToCMYK(event.data.colorHex);
				if (iframeRef.current?.contentWindow) {
					iframeRef.current.contentWindow.postMessage(
						{ type: "respuesta", data: valor },
						"*"
					);
				}
			}
			// Mensaje del popup de login (cuando se completa el login desde el iframe)
			if (event.data.type === "LOGIN_SUCCESS") {
				// Reenviar el mensaje al iframe
				if (iframeRef.current?.contentWindow) {
					iframeRef.current.contentWindow.postMessage(
						{
							type: "LOGIN_SUCCESS",
							userId: event.data.userId,
							token: event.data.token,
						},
						"*"
					);
				}
				// Actualizar el idUser local si está disponible
				if (event.data.userId) {
					setIdUser(event.data.userId);
					localStorage.setItem("idUser", event.data.userId);
				}
				// Intentar cerrar el popup desde la ventana padre
				// Buscar el popup en window y cerrarlo
				setTimeout(() => {
					try {
						// Buscar el popup en la lista de ventanas
						if (window.popupLogin && !window.popupLogin.closed) {
							window.popupLogin.close();
						} else if (
							event.source &&
							event.source !== window &&
							event.source !== window.parent
						) {
							// Intentar cerrar usando la referencia de la ventana fuente
							try {
								event.source.close();
							} catch (e) {
								// Silenciar error al cerrar popup
							}
						}
					} catch (e) {
						// Silenciar error al intentar cerrar popup
					}
				}, 100);
			}
		};

		window.addEventListener("message", handleMessage);
		return () => {
			window.removeEventListener("message", handleMessage);
		};
	}, []);

	const convertirHExToCMYK = (colorHex) => {
		const selectedColor = colorHex;
		try {
			const cmykColor = convert.hex.cmyk(selectedColor);
			return cmykColor;
		} catch (error) {
			return { error: "No es posible convertir a CMYK: " + error.message };
		}
	};

	// Construir URL del iframe con el token
	// Cuando tipo es "2" (rediseñar), el id debe ser el ID del diseño, no del modelo
	const idParaUrl = (tipo === "2" && desdePerfil) ? idModelo : idModelo;
	const urlDiseñador =
		`${Globales.Url}componente3d` +
		`?id=${idParaUrl || ""}` +
		`&tipo=${tipo || "1"}` +
		`&idTalla=${idTalla || ""}` +
		`&idUser=${idUser || ""}` +
		`&nombre=${btoa(nombre || "")}` +
		`&categoria=${idCategoria || ""}` +
		`&descripcion=${btoa(descripcion || "")}` +
		(desdePerfil ? `&desdeperfil=true` : "") +
		(authToken ? `&token=${authToken}` : "") +
		(sessionId && !idUser ? `&sessionId=${sessionId}` : "");

	// Manejar cuando el iframe carga
	const handleIframeLoad = () => {
		// Enviar token por postMessage cuando el iframe esté listo
		if (authToken && iframeRef.current?.contentWindow) {
			// Solo enviar userId si es un ObjectId válido (no enviar user?.sub que es el socialToken)
			const userIdToSend =
				idUser && /^[0-9a-fA-F]{24}$/.test(idUser) ? idUser : null;

			if (userIdToSend) {
				setTimeout(() => {
					iframeRef.current.contentWindow.postMessage(
						{
							type: "AUTH0_TOKEN",
							token: authToken,
							userId: userIdToSend,
						},
						"*"
					);
				}, 500); // Pequeño delay para asegurar que el iframe esté listo
			}
		}

		// Enviar sessionId al iframe si existe y no hay usuario autenticado
		if (sessionId && !idUser && iframeRef.current?.contentWindow) {
			setTimeout(() => {
				iframeRef.current.contentWindow.postMessage(
					{
						type: "SESSION_ID",
						sessionId: sessionId,
					},
					"*"
				);
			}, 500);
		}
	};

	// Altura del Header (60px según Header.jsx)
	const HEADER_HEIGHT = 60;

	// Asegurar que el body no tenga padding/margin cuando se monta el componente
	useEffect(() => {
		// Guardar estilos originales del body
		const originalBodyStyle = {
			margin: document.body.style.margin,
			padding: document.body.style.padding,
			overflow: document.body.style.overflow,
		};

		// Aplicar estilos para fullscreen (pero mantener espacio para Header)
		document.body.style.margin = "0";
		document.body.style.padding = "0";
		document.body.style.overflow = "hidden";

		// Limpiar al desmontar
		return () => {
			document.body.style.margin = originalBodyStyle.margin;
			document.body.style.padding = originalBodyStyle.padding;
			document.body.style.overflow = originalBodyStyle.overflow;
		};
	}, []);

	return (
		<div
			style={{
				position: "fixed",
				top: `${HEADER_HEIGHT}px`, // Dejar espacio para el Header
				left: 0,
				width: "100vw",
				height: `calc(100vh - ${HEADER_HEIGHT}px)`, // Altura total menos Header
				zIndex: 1000,
				margin: 0,
				padding: 0,
				overflow: "hidden",
			}}
		>
			<iframe
				ref={iframeRef}
				id="iframePrincipalDiseno"
				onLoad={handleIframeLoad}
				style={{
					width: "100%",
					height: "100%",
					position: "absolute",
					top: 0,
					left: 0,
					border: "none",
					margin: 0,
					padding: 0,
				}}
				src={urlDiseñador}
				title="Diseñador 3D"
				allowFullScreen
			>
				<p>Your browser does not support iframes.</p>
			</iframe>
		</div>
	);
}

export default ProductPage;
